import {
  PARTNER_API_BASE_URL,
  UNDERWRITING_BASE_URL,
  SOURCES
} from '../constants/global';
import moment from 'moment';
import STANDARD_FORM_FIELDS from '../constants/fieldsForStandardSubmissionApiLead';
import SHORTENED_FORM_FIELDS from '../constants/fieldsForShortSubmissionApiLead';
import { ZIP_AUTHORIZED_LENGTHS, PHONE_AUTHORIZED_LENGTH } from '../constants/fieldsConstants';

const STRING_TO_REPLACE = 'Info';
const FIELD_TYPE_FILES = {
  standard_form: STANDARD_FORM_FIELDS,
  shortened_form: SHORTENED_FORM_FIELDS
};

// same email regex used in the Rails apps (see: https://www.rubydoc.info/stdlib/uri/URI/MailTo)
const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

export const validateEmail = (val) => val? emailRegex.test(val) : true;

/**
 * Remove non-digit characters from given string
 * @param {String} value
 * @returns {String}
 */
const digitizeString = (value = "") => {
  return value.replace(/\D+/g, '');
};

/**
 * Remove non-digit characters from given phone string
 * @param {String} phone
 * @returns {String}
 */
const normalizedPhone = (phone="") => {
  return digitizeString(phone) || '';
};

const normalizedZip = (zip="") => {
  return digitizeString(zip);
};

/**
 * Convert string ownership to integer. Default to 0
 * @param {String} ownership
 * @returns {Integer}
 */
const normalizedNumericOwnership = (ownership="") => {
  return parseInt(digitizeString(ownership), 10) || 0;
}

/**
 * validate zip length
 * Note:
 *  - should let pass when there is no input,
 *    because no input is concern of requirement check, not length check.
 * @param {String|undefined} zip
 * @returns {bool}
 */
export const validateZipLength = (zip) => {
  const stringifiedZip = normalizedZip(zip);
  const authorizedLengths = Object.values(ZIP_AUTHORIZED_LENGTHS);

  // should let non-input pass, because non-input is to `required`'s concern.
  return !stringifiedZip || authorizedLengths.includes(stringifiedZip.length);
};

/**
 * validate phone length
 * Note:
 *  - should let pass when there is no input,
 *    because no input is concern of requirement check, not length check.
 * @param {String|undefined} phone
 * @returns {bool}
 */
export const validatePhoneLength = (phone) => {
  const stringifiedPhone = normalizedPhone(phone);

  // should let non-input pass, because non-input is to `required`'s concern.
  return !stringifiedPhone || stringifiedPhone.length === PHONE_AUTHORIZED_LENGTH;
};

export const validateOwnership = (ownership="") => {
  const ownershipInt = parseInt(digitizeString(ownership), 10);

  return ownershipInt <= 100 && ownershipInt > 0;
};

function fieldPrefix({label, iteration, fieldLabel}) {
  if (label) {
    return `${label}${iteration}_${fieldLabel}`;
  } else {
    return fieldLabel;
  }
}

function fieldValue({data, label, iteration, fieldLabel}) {
  return data[fieldPrefix({label, iteration, fieldLabel})];
}

function fieldKey(fieldLabel) {
  if (fieldLabel && typeof fieldLabel === 'string') {
    return fieldLabel.replace(STRING_TO_REPLACE, '_');
  }
}

function mapFields({fieldType='', data, iteration=0, source=SOURCES['STANDARD']}) {
  const findFields = FIELD_TYPE_FILES[source][fieldType].fields;

  if (findFields && findFields.length > 0) {
    const label = FIELD_TYPE_FILES[source][fieldType].label;
    return findFields.reduce((generatedFields, fieldLabel) => {
      const key = fieldKey(fieldLabel);

      generatedFields[key] = fieldValue({data, label, iteration, fieldLabel});

      return generatedFields;
    }, {});
  }
}

function formatZip(zip="") {
  const stringifiedZip = normalizedZip(zip);
  const { LONG } = ZIP_AUTHORIZED_LENGTHS;

  if (stringifiedZip && stringifiedZip.length === LONG) {
    return zip;
  }

  return stringifiedZip || "";
}

function fallbackField(value) {
  return value || "";
}

function formatDate(value) {
  const formattedDate = moment(value);

  if (formattedDate.isValid()) {
    return moment(value).format('YYYY-MM-DD');
  } else {
     return fallbackField();
  }
}

function setLegalName(data) {
  if (data.sameDbaAndLegalName) {
    return data.name;
  } else {
    return data.legal_name;
  }
}

function currentAddressAttributes(address) {
  return {
    "current_address_attributes": {
      ...address,
      "street2": fallbackField(address["street2"]),
      "zip": formatZip(address["zip"])
    }
  };
}

function mapContactsFromOwners(data, source=SOURCES['STANDARD']) {
  const owners = data.owners;
  if (owners) {
    return owners.map((owner) => {
      const contact = mapFields({ fieldType: 'CONTACT', data: owner, source});
      const address = mapFields({ fieldType: 'CONTACT_ADDRESS', data: owner, source});
      return {
        ...contact,
        "born_on": formatDate(contact["born_on"]),
        "ownership_date": formatDate(owner[`ownership_date`]),
        "title": "",
        ...currentAddressAttributes(address)
      };
    });
  }
}

export default {
  formatDataForSubmissionToLeads(data) {
    const contacts = mapContactsFromOwners(data);
    // NOTE: currently only owner_1 and owner_2 percent_ownership is persisted
    const account = mapFields({fieldType: 'ACCOUNT', data});
    const accountAddress = mapFields({fieldType: 'ACCOUNT_ADDRESS', data});
    const application = mapFields({fieldType: 'APPLICATION', data});

    return {
        "lead": {
          "contacts_attributes": contacts,
          "account_attributes": {
            ...account,
            "legal_name": setLegalName(data),
            "started_on": formatDate(account["started_on"]),
            ...currentAddressAttributes(accountAddress),
          },
          "application_attributes": {
            ...application,
            "owner_1_percent_ownership": normalizedNumericOwnership(application["owner_1_percent_ownership"]),
            "owner_2_percent_ownership": normalizedNumericOwnership(application["owner_2_percent_ownership"])
          }
        }
    };
  },

  formatDataForShortenedSubmissionToLeads(data) {
    let source = SOURCES['SHORT'];
    const account = mapFields({fieldType: 'ACCOUNT', data, source});
    const application = mapFields({fieldType: 'APPLICATION', data, source});
    const contact = mapFields({fieldType: 'CONTACT', data: data.owners[0], source});

    return {
      "lead": {
        "account_attributes": {
          ...account
        },
        "application_attributes": {
          ...application
        },
        "contacts_attributes": [contact],
      }
    };
  },

  formatDataForSubmissionToStips(inputs, files, uuid) {
    const inputStips = inputs.map((stip) => {
      return {kind: "input", uuid: stip.uuid, type: stip.display, value: stip.value};
    });
    const uploadStips = files.map((file) => {
      return {kind: "upload", type: file.tag, uuid: file.uuid, value: file.name};
    });
    return {stips_updates: {opp_id: uuid, stips: inputStips.concat(uploadStips)}};
  },

  formatFilenameForAttachments(filename, tag) {
    const getFilenameWithoutExtension = this.getFilenameWithoutExtension(filename);
    const filenameForStips = this.formatFilenameForStips(filename, tag);
    return `${getFilenameWithoutExtension}_${filenameForStips}`;
  },

  formatFilenameForStips(filename, tag) {
    const extension = this.getFilenameExtension(filename);
    const time = moment(new Date()).format('MM-DD-YY-HH-mm-SSS');
    return `${this.defineAndNormalizeTag(tag)}_${time}.${extension}`;
  },

  splitFilenameWithExtension(filename) {
    return filename.split(".");
  },

  getFilenameWithoutExtension(filename) {
   return this.splitFilenameWithExtension(filename)[0];
  },

  getFilenameExtension(filename) {
    return this.splitFilenameWithExtension(filename).pop();
  },

  defineAndNormalizeTag(tag) {
    if (!tag || tag === ''){
      tag = 'Untagged';
    }
    return tag.replace(/[&/\\#,+()$~%.'":*?<>{}]/g,'_');
  },

  newSubmissionsURL() {
    return `${PARTNER_API_BASE_URL()}v1/lead`;
  },

  attachmentSubmissionUrl(){
    return `${PARTNER_API_BASE_URL()}v1/attachment`;
  },
  newSubmissionPresignedURLEndpoint(id, name){
    return `${PARTNER_API_BASE_URL()}v1/presigned_url?lead_id=${id}&filename=${name}&encoded=false`;
  },

  shortenedNewSubmissionsURL() {
    return `${PARTNER_API_BASE_URL()}v1/short_lead`;
  },

  renewalSubmissionsURL() {
    return `${PARTNER_API_BASE_URL()}v1/renewal`;
  },
  underwritingRenewalSubmissionsURL() {
    return `${UNDERWRITING_BASE_URL()}api/v2/renewal_submissions`;
  },
  renewalPresignedURLEndpoint(name){
    return `${PARTNER_API_BASE_URL()}v1/presigned_url?filename=${name}&encoded=false`;
  },

  stipSubmissionsURL() {
    return `${PARTNER_API_BASE_URL()}v1/update_stips`;
  },
  stipPresignedURLEndpoint(id, filename){
    return `${PARTNER_API_BASE_URL()}v1/presigned_url?stip_opportunity_uuid=${id}&filename=${filename}&encoded=false`;
  }
};
